import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes, useLocation, type Location } from "react-router";
import { ErrorBoundary, ToastProvider } from "@astrum/ui";
import "@astrum/ui/tokens.css";
import "./a11y.css";
import { describeToastError, initErrorTracking, reportError, reportNavigationTiming } from "./lib/error-report.js";
import { CrashScreen } from "./shell/CrashScreen.js";
import { AuthProvider, useAuth } from "./lib/auth.js";
import { lockNativeZoom, startNativeBackButton, startNativeKeyboard } from "./lib/native-app.js";
import { BrandingProvider } from "./lib/branding.js";
import { WorkspaceProvider } from "./lib/workspace.js";
import { RealtimeProvider } from "./lib/realtime-context.js";
import { CommandPaletteProvider } from "./shell/CommandPalette.js";
import { AppShell } from "./shell/AppShell.js";
import { Chat } from "./routes/Chat.js";
import { Login } from "./routes/Login.js";
import { RequireAuth } from "./routes/RequireAuth.js";
import { Work } from "./routes/Work.js";
import { Checklist } from "./routes/Checklist.js";
import { DailyReport } from "./routes/DailyReport.js";
import { DailyReportTeam } from "./routes/DailyReportTeam.js";
import { DailyReportPerson } from "./routes/DailyReportPerson.js";
import { TaskDetail } from "./routes/TaskDetail.js";
import { ProjectDetail } from "./routes/ProjectDetail.js";
import { DocRedirect } from "./routes/DocRedirect.js";
import { Schedule } from "./routes/Schedule.js";
import { People } from "./routes/People.js";
import { OrgChart } from "./routes/OrgChart.js";
import { Feed } from "./routes/Feed.js";
import { Search } from "./routes/Search.js";
import { Files } from "./routes/Files.js";
import { Home } from "./routes/Home.js";
import { Help } from "./routes/Help.js";
import { Profile } from "./routes/Profile.js";
import { Insights } from "./routes/Insights.js";
import { FormPage } from "./routes/FormPage.js";
import { Requests } from "./routes/Requests.js";
import { Goals } from "./routes/Goals.js";
import { Dashboards } from "./routes/Dashboards.js";
import { DashboardDetail } from "./routes/DashboardDetail.js";
import { Timesheet } from "./routes/Timesheet.js";
import { Admin } from "./routes/Admin.js";
import { AcceptInvite } from "./routes/AcceptInvite.js";
import { ForgotPassword } from "./routes/ForgotPassword.js";
import { ResetPassword } from "./routes/ResetPassword.js";
import { ChangePassword } from "./routes/ChangePassword.js";
import { ShareStatus } from "./routes/ShareStatus.js";

/** `/login` redirects away immediately if a session already exists — no
 * point showing the form to someone who's already signed in. Back to the page
 * that sent them here when there is one: this redirect runs the moment
 * signing in sets the viewer, before Login's own navigate, so a fixed "/home"
 * here threw away every deep link and every "pick up where you left off". */
function LoginRoute() {
  const { viewer, loading } = useAuth();
  const location = useLocation();
  if (!loading && viewer) {
    const from = (location.state as { from?: Location } | null)?.from;
    return <Navigate to={from ? `${from.pathname}${from.search ?? ""}${from.hash ?? ""}` : "/home"} replace />;
  }
  return <Login />;
}

/**
 * Administration is admin-only.
 *
 * The API enforces this on every endpoint; this guard exists so a non-admin
 * who follows a link gets sent home rather than a screen of failed requests.
 */
function AdminRoute() {
  const { viewer, loading } = useAuth();
  if (loading) return null;
  if (viewer?.role !== "admin") return <Navigate to="/home" replace />;
  return <Admin />;
}

function App() {
  return (
    <AuthProvider>
      {/* Inside AuthProvider (it fetches /workspaces, which needs a session)
          but above BrandingProvider, which reads the active office's name from
          it. Renders children while its own fetch is in flight — the switcher
          and branding both no-op until the list resolves — so it does not
          block on auth's loading flag. */}
      <WorkspaceProvider>
      {/* Outside RequireAuth: the login screen needs the workspace name and
          logo before there is a session to read them with. */}
      <BrandingProvider>
      <RealtimeProvider>
        <ToastProvider describeError={describeToastError}>
          <CommandPaletteProvider>
            <Routes>
              <Route path="/login" element={<LoginRoute />} />
              {/* Unauthenticated by necessity — the recipient has no account
                  yet, which is the point of the page. */}
              <Route path="/invite/:token" element={<AcceptInvite />} />
              {/* Also unauthenticated: forgetting a password is the one thing
                  that happens precisely when signing in is not an option. */}
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              {/* Public by design: a read-only status page for whoever holds the link. */}
              <Route path="/share/status/:token" element={<ShareStatus />} />

              <Route element={<RequireAuth />}>
                {/* Outside the shell: on a forced change there is nowhere else
                    to navigate, and offering a nav rail would be a lie. */}
                <Route path="change-password" element={<ChangePassword />} />

                <Route element={<AppShell />}>
                  <Route index element={<Home />} />
                  <Route path="home" element={<Home />} />
                  <Route path="chat" element={<Chat />} />
                  <Route path="chat/:channelId" element={<Chat />} />
                  {/* A personal "my tasks everywhere" view, Lark-style — same
                      screen as Work, just without the project browser and
                      defaulted to what's assigned to you. */}
                  <Route path="tasks" element={<Work personalOnly />} />
                  <Route path="work" element={<Work />} />
                  <Route path="work/task/:taskId" element={<TaskDetail />} />
                  <Route path="work/project/:projectId" element={<ProjectDetail />} />
                  <Route path="work/doc/:docId" element={<DocRedirect />} />
                  <Route path="checklist" element={<Checklist />} />
                  <Route path="daily-report" element={<DailyReport />} />
                  <Route path="daily-report/team" element={<DailyReportTeam />} />
                  <Route path="daily-report/team/:userId" element={<DailyReportPerson />} />
                  <Route path="insights" element={<Insights />} />
                  <Route path="goals" element={<Goals />} />
                  <Route path="forms" element={<Requests />} />
                  <Route path="forms/:formId" element={<FormPage />} />
                  <Route path="dashboards" element={<Dashboards />} />
                  <Route path="dashboards/:dashboardId" element={<DashboardDetail />} />
                  <Route path="timesheet" element={<Timesheet />} />
                  <Route path="schedule" element={<Schedule />} />
                  <Route path="people" element={<People />} />
                  <Route path="people/org-chart" element={<OrgChart />} />
                  <Route path="feed" element={<Feed />} />
                  <Route path="search" element={<Search />} />
                  <Route path="files" element={<Files />} />
                  <Route path="help" element={<Help />} />
                  <Route path="profile" element={<Profile />} />
                  <Route path="admin" element={<AdminRoute />} />
                </Route>
              </Route>

              {/* Every component in every state — the visual regression surface. */}
            </Routes>
          </CommandPaletteProvider>
        </ToastProvider>
      </RealtimeProvider>
      </BrandingProvider>
      </WorkspaceProvider>
    </AuthProvider>
  );
}

const root = document.getElementById("root");
if (!root) throw new Error("Missing #root element");

// Starts nothing unless VITE_SENTRY_DSN was set at build time.
initErrorTracking();
lockNativeZoom();
startNativeBackButton();
startNativeKeyboard();
reportNavigationTiming();

createRoot(root).render(
  <StrictMode>
    {/* Outermost safety net: a throw in the providers, the login screen, or
        anywhere above the shell would otherwise blank the whole window —
        especially in the desktop app, where there is no browser chrome to
        reload from. The per-route boundary inside AppShell handles the
        common case of one screen failing. */}
    <ErrorBoundary fallback={(error) => <CrashScreen error={error} />} onError={(error) => reportError(error)}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>,
);
